import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 4000,
    };

    setToasts(prev => [newToast, ...prev.slice(0, 2)]); // Keep max 3 toasts

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, [removeToast]);

  const success = useCallback((title: string, description?: string) => {
    addToast({ type: 'success', title, description });
  }, [addToast]);

  const error = useCallback((title: string, description?: string) => {
    addToast({ type: 'error', title, description });
  }, [addToast]);

  const warning = useCallback((title: string, description?: string) => {
    addToast({ type: 'warning', title, description });
  }, [addToast]);

  const info = useCallback((title: string, description?: string) => {
    addToast({ type: 'info', title, description });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed left-1/2 transform -translate-x-1/2 z-[9999] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm top-20 sm:top-4">
      {toasts.map((toast, index) => (
        <ToastItem 
          key={toast.id} 
          toast={toast} 
          onRemove={removeToast}
          style={{ animationDelay: `${index * 50}ms` }}
        />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove, style }: { toast: Toast; onRemove: (id: string) => void; style?: React.CSSProperties }) {
  const config = {
    success: {
      bg: 'bg-emerald-600',
      icon: <CheckCircle className="h-5 w-5 text-white" />,
    },
    error: {
      bg: 'bg-red-600',
      icon: <XCircle className="h-5 w-5 text-white" />,
    },
    warning: {
      bg: 'bg-amber-600',
      icon: <AlertTriangle className="h-5 w-5 text-white" />,
    },
    info: {
      bg: 'bg-blue-600',
      icon: <Info className="h-5 w-5 text-white" />,
    },
  };

  const { bg, icon } = config[toast.type];

  return (
    <div
      className={`
        ${bg} text-white rounded-lg shadow-lg overflow-hidden
        animate-[toast-in_0.3s_ease-out_forwards]
      `}
      style={style}
      dir="rtl"
    >
      <div className="flex items-center gap-3 p-3">
        <div className="flex-shrink-0">
          {icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {toast.title}
          </p>
          {toast.description && (
            <p className="text-xs opacity-90 truncate mt-0.5">
              {toast.description}
            </p>
          )}
        </div>

        <button
          onClick={() => onRemove(toast.id)}
          className="flex-shrink-0 p-1 rounded hover:bg-white/20 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      
      {/* Progress bar */}
      <div className="h-1 bg-white/30">
        <div 
          className="h-full bg-white/60 animate-[progress_4s_linear_forwards]"
        />
      </div>
    </div>
  );
}